import type { AnnualTimetableCell } from '../types/domain';
import { buildAnnualProgressLearningCrossSuffix } from './annualProgressCrossTopicExtract';

export type NeisLearningContentUploadSourceRow = {
  dateIso: string;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  dayRowOrder: number;
  slotIndex: number;
  mondayIso: string;
  weekdayKey: string;
  gradeLabel: string;
  semesterLabel: string;
  templateLabel: string;
  timetableSlotKind: AnnualTimetableCell['slot_kind'];
  progressUnit: string;
  progressLearning: string;
  progressCross: string;
  progressCrossNote: string;
  progressMemoPrimary: string;
  progressMemoNote: string;
  progressPage: string;
  progressAssistantPage: string;
  progressCurrentPeriod: string;
  progressTotalPeriod: string;
  progressMaterial: string;
  /** 편제 동기화 미러(대표 시간표가 아닌 alias) — 제외 시 생략 */
  isSyncTimetableMirror?: boolean;
};

export type NeisLearningContentUploadRow = {
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  gradeLabel: string;
  semesterLabel: string;
  templateLabel: string;
  unit: string;
  learning: string;
  page: string;
  assistantPage: string;
  currentPeriod: string;
  totalPeriod: string;
  material: string;
  mondayIso: string;
  weekdayKey: string;
  slotIndex: number;
  dayRowOrder: number;
  dateIso: string;
};

export type NeisUploadLearningContentRow = NeisLearningContentUploadRow & {
  trackSeq: number;
};

export type NeisLearningContentUploadViewMode = 'annual-plan' | 'neis-upload';

function shouldIncludeSlotKind(
  slotKind: AnnualTimetableCell['slot_kind'],
  params: { includeActivity: boolean; includeNonClass: boolean }
): boolean {
  if (slotKind === 'activity') return params.includeActivity;
  if (slotKind === 'non_class') return params.includeNonClass;
  return true;
}

function buildMemoAppendText(memoPrimary: string, memoNote: string): string {
  const primary = memoPrimary.trim();
  const note = memoNote.trim();
  if (primary && note) return `${primary} ${note}`;
  return primary || note;
}

/** 연간진도표 프로그램 내용 열과 동일하게 범교과·메모를 본문에 합친다 */
export function buildNeisProgramLearningDisplayText(params: {
  learning: string;
  crossTopic: string;
  crossNote: string;
  memoPrimary: string;
  memoNote: string;
}): string {
  const crossSuffix = buildAnnualProgressLearningCrossSuffix(params.crossTopic, params.crossNote);
  const memoText = buildMemoAppendText(params.memoPrimary, params.memoNote);
  return [params.learning.trim(), crossSuffix.trim(), memoText.trim()].filter(Boolean).join(' ').trim();
}

export function buildSubscribedTrackFilterOptions(trackNames: string[]): string[] {
  return Array.from(new Set(trackNames.map((name) => name.trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, 'ko')
  );
}

export function filterNeisLearningContentUploadRows(
  sourceRows: NeisLearningContentUploadSourceRow[],
  params: {
    selectedTrack: string | null;
    includeActivity: boolean;
    includeNonClass: boolean;
    /** 기본 true. false면 동기화 미러 시간표 행 생략 */
    includeSyncTimetable?: boolean;
    subscribedTrackNames: Set<string>;
  }
): NeisLearningContentUploadRow[] {
  const {
    selectedTrack,
    includeActivity,
    includeNonClass,
    includeSyncTimetable = true,
    subscribedTrackNames,
  } = params;
  const out: NeisLearningContentUploadRow[] = [];

  for (const row of sourceRows) {
    if (row.timetableSlotKind === 'holiday') continue;

    const templateLabel = row.templateLabel.trim();
    if (!templateLabel || !subscribedTrackNames.has(templateLabel)) continue;
    if (selectedTrack && templateLabel !== selectedTrack) continue;

    if (!shouldIncludeSlotKind(row.timetableSlotKind, { includeActivity, includeNonClass })) continue;
    if (!includeSyncTimetable && row.isSyncTimetableMirror) continue;

    out.push({
      dateLabel: row.dateLabel.trim(),
      weekdayLabel: row.weekdayLabel.trim(),
      periodLabel: row.periodLabel.trim(),
      gradeLabel: row.gradeLabel.trim(),
      semesterLabel: row.semesterLabel.trim(),
      templateLabel,
      unit: row.progressUnit.trim(),
      learning: buildNeisProgramLearningDisplayText({
        learning: row.progressLearning,
        crossTopic: row.progressCross,
        crossNote: row.progressCrossNote,
        memoPrimary: row.progressMemoPrimary,
        memoNote: row.progressMemoNote,
      }),
      page: row.progressPage.trim(),
      assistantPage: row.progressAssistantPage.trim(),
      currentPeriod: row.progressCurrentPeriod.trim(),
      totalPeriod: row.progressTotalPeriod.trim(),
      material: row.progressMaterial.trim(),
      mondayIso: row.mondayIso,
      weekdayKey: row.weekdayKey,
      slotIndex: row.slotIndex,
      dayRowOrder: row.dayRowOrder,
      dateIso: row.dateIso,
    });
  }

  out.sort((a, b) => {
    const dateCmp = a.dateIso.localeCompare(b.dateIso);
    if (dateCmp !== 0) return dateCmp;
    if (a.dayRowOrder !== b.dayRowOrder) return a.dayRowOrder - b.dayRowOrder;
    return a.slotIndex - b.slotIndex;
  });

  return out;
}

function compareNeisRowsBySchedule(a: NeisLearningContentUploadRow, b: NeisLearningContentUploadRow): number {
  const dateCmp = a.dateIso.localeCompare(b.dateIso);
  if (dateCmp !== 0) return dateCmp;
  if (a.dayRowOrder !== b.dayRowOrder) return a.dayRowOrder - b.dayRowOrder;
  return a.slotIndex - b.slotIndex;
}

/** NEIS 업로드: 편제 순서대로 묶고, 편제마다 날짜·교시 순으로 정렬하며 순을 다시 매긴다 */
export function buildNeisUploadLearningContentRows(
  rows: NeisLearningContentUploadRow[],
  trackOrder: string[]
): NeisUploadLearningContentRow[] {
  const byTrack = new Map<string, NeisLearningContentUploadRow[]>();
  for (const row of rows) {
    const list = byTrack.get(row.templateLabel) ?? [];
    list.push(row);
    byTrack.set(row.templateLabel, list);
  }

  const out: NeisUploadLearningContentRow[] = [];
  for (const trackName of trackOrder) {
    const trackRows = byTrack.get(trackName);
    if (!trackRows?.length) continue;
    const sorted = [...trackRows].sort(compareNeisRowsBySchedule);
    sorted.forEach((row, index) => {
      out.push({ ...row, trackSeq: index + 1 });
    });
  }
  return out;
}

export function neisLearningContentUploadRowsHaveData(rows: NeisLearningContentUploadRow[]): boolean {
  return rows.some(
    (row) =>
      row.unit ||
      row.learning ||
      row.page ||
      row.assistantPage ||
      row.currentPeriod ||
      row.totalPeriod ||
      row.material
  );
}
